import { RefObject, useEffect, useMemo, useRef, useState } from 'react';

import { ProcessingStatus } from 'enums';
import { useAppSelector, useTimecodes, useTypedDispatch } from 'hooks';
import {
  FileType,
  UserFileType,
  UserInspectionType,
  editUserFile,
  editUserInspection,
  selectCertainInspection,
  selectFiles,
  selectIsPlaying,
} from 'state';
import { setIsPlaying } from 'state/actions';
import {
  convertNumberToTime,
  getFileAtTime,
  getFileStartAndEndTime,
  getInspectionDuration,
  shortenTimeString,
} from 'utils';

export type VideoPlayerHookType = {
  currentFile?: FileType;
  /** how far the player has advanced within the inspection file */
  fileProgress: number;
  /** if we consider all the files in an inspection as one big file, this is how far the player has progressed through that */
  inspectionProgress: number;
  furthestInspectionProgress: number;
  togglePlay: () => void;
  handleManualInspectionProgress: (timeWithinInspection: number) => void;
  advanceVideo: (delta: number) => void;
  progressBar: string;
  isVideoLoaded: boolean;
  setVideoLoaded: (isLoaded: boolean) => void;
  setFileProgress: (progress: number) => void;
  goToNextVideo: (wasPlaying?: boolean) => void;
  notification?: string;
};

export const useVideoPlayer = (
  videoElement: RefObject<HTMLVideoElement>,
): VideoPlayerHookType => {
  const files = useAppSelector(selectFiles);
  const inspection = useAppSelector(selectCertainInspection);
  const dispatch = useTypedDispatch();
  const isPlaying = useAppSelector(selectIsPlaying);
  const { inspectionDuration: duration } = useTimecodes();
  const [currentFile, setCurrentFile] = useState(files[0]);
  const [furthestFileProgress, setFurthestFileProgress] = useState<number>(0);
  const [furthestInspectionProgress, setFurthestInspectionProgress] = useState<number>(0);
  const [isVideoLoaded, setVideoLoaded] = useState(false);
  const [fileProgress, setFileProgress] = useState<number>(0);
  const [notification, setNotification] = useState<string | undefined>();
  const [throttledProgress, setThrottledProgress] = useState<number | undefined>();

  const togglePlay = () => {
    if (isPlaying || !isVideoLoaded) {
      dispatch(setIsPlaying(false));
    } else {
      dispatch(setIsPlaying(true));
    }
  };

  const inspectionProgress = useMemo(() => {
    const fileStartTime = currentFile
      ? getFileStartAndEndTime(files, currentFile.id).fileStartTime
      : 0;

    return fileStartTime + fileProgress;
  }, [currentFile, fileProgress, files]);

  const progressLastUpdated = useRef(Date.now());

  useEffect(() => {
    // if the files change, pause the video
    setIsPlaying(false);

    setFurthestInspectionProgress(
      Math.max(
        inspection?.userInspection?.furthestProgress || 0,
        furthestInspectionProgress,
      ),
    );

    // set current file & file progress
    const previousCurrentProgress = inspection?.userInspection?.currentProgress;

    if (previousCurrentProgress) {
      const { file, timeWithinFile } = getFileAtTime(files, previousCurrentProgress);

      if (file) setCurrentFile(file);
      setFileProgress(timeWithinFile);
    } else if (
      !currentFile.uploadCompleted ||
      currentFile.processingStatus !== ProcessingStatus.PROCESSED
    ) {
      // if the expected current file hasn't finished processing, start instead with the first file that has
      const firstProcessedFile = files.find(
        f => f.uploadCompleted && f.processingStatus === ProcessingStatus.PROCESSED,
      );

      if (firstProcessedFile) {
        setCurrentFile(firstProcessedFile);
        setFileProgress(0);
      }
    } else if (currentFile?.inspectionId !== inspection.id) {
      setCurrentFile(files[0]);
      setFileProgress(0);
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [files, inspection]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (Date.now() - progressLastUpdated.current >= 2000) {
        setThrottledProgress(fileProgress);
        progressLastUpdated.current = Date.now();
      }
    }, 2000 - (Date.now() - progressLastUpdated.current));

    return () => clearTimeout(handler);
  }, [fileProgress]);

  useEffect(() => {
    if (!currentFile?.id || !isVideoLoaded) return;

    const newUserFile: UserFileType = {
      currentProgress: fileProgress,
      inspectionFileId: currentFile.id,
    };

    newUserFile.furthestProgress = Math.max(
      furthestFileProgress || 0,
      newUserFile?.currentProgress || 0,
    );
    dispatch(editUserFile(newUserFile));

    const newUserInspection: UserInspectionType = {
      currentProgress: inspectionProgress,
      inspectionId: inspection.id,
    };

    newUserInspection.furthestProgress = Math.max(
      furthestInspectionProgress || 0,
      newUserInspection?.currentProgress || 0,
    );
    dispatch(
      editUserInspection(newUserInspection),
    ); /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [currentFile?.id, dispatch, inspection?.id, isVideoLoaded, throttledProgress]);

  // if the video is advanced beyond its farthest previous point, update furthestFileProgress
  useEffect(() => {
    if (fileProgress > furthestFileProgress) {
      setFurthestFileProgress(fileProgress);
    }
  }, [fileProgress, furthestFileProgress]);

  // if the inspection is advanced beyond its farthest previous point, update furthestInspectionProgress
  useEffect(() => {
    if (inspectionProgress > furthestInspectionProgress) {
      setFurthestInspectionProgress(inspectionProgress);
    }
  }, [furthestInspectionProgress, inspectionProgress]);

  const advanceVideo = (delta: number = 1) => {
    const nextTime = Math.max(inspectionProgress + delta, 0);
    const inspectionDuration = getInspectionDuration(files);

    handleManualInspectionProgress(
      nextTime > inspectionDuration ? inspectionDuration : nextTime,
    );
  };

  // NOTE: changeVideo should only be used from within handleManualInspectionProgress or goToNextVideo
  const changeVideo = (file: FileType, timeWithinFile?: number) => {
    if (
      (file.processingStatus !== ProcessingStatus.PROCESSED &&
        file.processingStatus !== ProcessingStatus.ERRORED) ||
      !file.uploadCompleted
    ) {
      setNotification(`File "${file.fileName}" is currently processing.`);
      setIsPlaying(false);

      return;
    }

    setCurrentFile(file);

    // we don't manaully set the videoElement time here, since the video is loading
    // instead it gets set in VideoPlayer.tsx, once the video has loaded
    setFileProgress(timeWithinFile || 0);
  };

  const goToNextVideo = (wasPlaying?: boolean) => {
    const indexOfCurrentFile = files.findIndex(f => f.id === currentFile?.id);
    const nextFile =
      indexOfCurrentFile + 1 < files.length ? files[indexOfCurrentFile + 1] : undefined;

    if (nextFile) {
      changeVideo(nextFile, 0);
      if (wasPlaying) setIsPlaying(true);
    }
  };

  const handleManualInspectionProgress = (timeWithinInspection: number) => {
    setNotification(undefined); // reset the notification

    // get the file & time using helper method
    // if the file is the current file, just use handleManualVideoProgress
    // if the file changed, switch files and then use handleManualVideoProgress
    const { file, timeWithinFile } = getFileAtTime(files, timeWithinInspection);

    if (!file) return;

    if (file.id === currentFile.id) {
      handleManualVideoProgress(timeWithinFile);
    } else {
      changeVideo(file, timeWithinFile);
    }
  };

  const handleManualVideoProgress = (manualChange: number) => {
    if (videoElement.current && manualChange !== fileProgress) {
      // eslint-disable-next-line no-param-reassign
      videoElement.current.currentTime = manualChange;

      if (manualChange > furthestFileProgress) {
        setFurthestFileProgress(manualChange);
      }
    }
  };

  const progressBar = useMemo(() => {
    const currentTime = shortenTimeString(
      duration,
      convertNumberToTime(inspectionProgress),
    );
    const durationAsTime = shortenTimeString(duration, convertNumberToTime(duration));

    return `${currentTime}/${durationAsTime}`;
  }, [inspectionProgress, duration]);

  return {
    currentFile,
    fileProgress,
    inspectionProgress,
    furthestInspectionProgress,
    togglePlay,
    handleManualInspectionProgress,
    advanceVideo,
    progressBar,
    isVideoLoaded,
    setVideoLoaded,
    setFileProgress,
    goToNextVideo,
    notification,
  };
};
